import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { RefreshCw, Timer, Eye, EyeOff, Monitor } from 'lucide-react';
import { toast } from 'sonner';

const STORAGE_KEY = 'qimam:auto-refresh-enabled';
const INTERVAL_SEC = 600;

// Manager-only page to toggle a 10-minute full-page auto-refresh cycle. Ideal
// for TV dashboards where the manager wants live data without touching the
// remote / keyboard. The interval is read by Layout.js and applied globally.
export default function AutoRefresh() {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [secondsLeft, setSecondsLeft] = useState(INTERVAL_SEC);

  // Live countdown so the manager can see the next tick.
  useEffect(() => {
    if (!enabled) { setSecondsLeft(INTERVAL_SEC); return; }
    setSecondsLeft(INTERVAL_SEC);
    const t = setInterval(() => {
      setSecondsLeft((s) => (s <= 1 ? INTERVAL_SEC : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [enabled]);

  const setState = (next) => {
    setEnabled(next);
    try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch {}
    window.dispatchEvent(new CustomEvent('auto-refresh:changed'));
    toast.success(next ? `تم التفعيل — تحديث كل ${INTERVAL_SEC} ثانية` : 'تم التعطيل');
  };

  return (
    <div className="space-y-6" dir="rtl" data-testid="auto-refresh-page">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl bg-brand_blue/10 flex items-center justify-center">
          <RefreshCw className="h-6 w-6 text-brand_blue" />
        </div>
        <div>
          <h1 className="text-2xl font-black">التحديث التلقائي</h1>
          <p className="text-sm text-foreground/60 mt-1">
            يُحدّث الصفحة تلقائياً كل {INTERVAL_SEC / 60} دقائق — مثالي لشاشات العرض الكبيرة (55" / 65") التي تعرض المؤشرات باستمرار.
          </p>
        </div>
      </div>

      <Card className="p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setState(true)}
            data-testid="auto-refresh-enable"
            className={`px-5 py-3 rounded-lg border-2 text-sm font-black flex items-center gap-2 transition-colors ${
              enabled ? 'border-emerald-600 bg-emerald-50 text-emerald-800' : 'border-input bg-background text-foreground hover:bg-accent'
            }`}
          >
            <Eye className="h-4 w-4" />
            تفعيل
          </button>
          <button
            type="button"
            onClick={() => setState(false)}
            data-testid="auto-refresh-disable"
            className={`px-5 py-3 rounded-lg border-2 text-sm font-black flex items-center gap-2 transition-colors ${
              !enabled ? 'border-destructive bg-destructive/10 text-destructive' : 'border-input bg-background text-foreground hover:bg-accent'
            }`}
          >
            <EyeOff className="h-4 w-4" />
            إيقاف
          </button>
        </div>

        <div className={`p-4 rounded-lg border-2 flex items-center gap-3 ${
          enabled ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-muted/40 border-input text-foreground/60'
        }`}>
          <Timer className="h-5 w-5 flex-shrink-0" />
          {enabled ? (
            <div>
              <p className="text-sm font-bold">التحديث نشط — سيتم إعادة تحميل الصفحة تلقائياً بعد</p>
              <p className="text-3xl font-black font-numbers mt-1" data-testid="auto-refresh-countdown">
                {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')} <span className="text-sm font-bold">دقيقة</span>
              </p>
            </div>
          ) : (
            <p className="text-sm font-bold">التحديث التلقائي مُعطّل — يمكنك تحديث الصفحة يدوياً بأي وقت.</p>
          )}
        </div>

        <div className="p-3 rounded-lg bg-brand_blue/5 border border-brand_blue/30 text-brand_blue text-xs leading-relaxed" data-testid="auto-refresh-hint">
          <Monitor className="h-4 w-4 inline-block ml-1" />
          <strong>ملاحظة:</strong> الإعداد يُحفظ في هذا المتصفح فقط. عند تشغيله على شاشة العرض
          الرئيسية سيبقى فعّالاً حتى بعد إعادة تحميل الجهاز. المُدد الأصغر من 10 دقائق ترهق النظام
          وقد تُخفض تجربة المستخدم.
        </div>
      </Card>
    </div>
  );
}
